import { DocumentIcon } from "@heroicons/react/24/outline";
import { Badge } from "@mantine/core";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
    BrokerTypes,
    MockOfferStruct,
} from "graphql/__generated__/globalTypes";
import numeral from "numeral";
import { FetchOffers_fetchOffers_data } from "../../graphql/queries/__generated__/FetchOffers";
import ClaimsActions from "./components/ClaimsActions";
import OffersActionMenu from "./components/OffersActionMenu";

export const useColumns = (brokerName: BrokerTypes, refresh?: () => void) => {
    const { t } = useTranslation();

    return useMemo(
        () => [
            {
                accessor: "",
                name: t("columns.closed_offers.policy_number_insured"),
                render: (offer: FetchOffers_fetchOffers_data | null) => (
                    <div className="flex items-center max-w-md ">
                        <div className="h-10 w-10 flex-shrink-0">
                            <DocumentIcon className="h-10 w-10 rounded-full" />
                        </div>
                        <div className="ml-4 break-all pl-3 truncate text-ellipsis">
                            <div className="font-medium truncate text-gray-900">
                                {offer?.offer_detail?.insured_by}
                            </div>
                            <div className="text-gray-500">
                                {offer?.offer_detail?.policy_number}
                            </div>
                        </div>
                    </div>
                ),
            },
            {
                accessor: "",
                name: t("columns.closed_offers.policy_period"),
                render: (offer: FetchOffers_fetchOffers_data | null) => (
                    <div className="flex items-center">
                        <div className="">
                            <div className="font-medium text-green-900">
                                {offer?.offer_detail?.period_of_insurance_from
                                    ? new Date(
                                          offer.offer_detail
                                              .period_of_insurance_from,
                                      ).toLocaleDateString("en-GB", {
                                          day: "2-digit",
                                          month: "2-digit",
                                          year: "numeric",
                                      })
                                    : ""}
                            </div>
                            <div className="text-red-500">
                                {offer?.offer_detail?.period_of_insurance_to
                                    ? new Date(
                                          offer.offer_detail
                                              .period_of_insurance_to,
                                      ).toLocaleDateString("en-GB", {
                                          day: "2-digit",
                                          month: "2-digit",
                                          year: "numeric",
                                      })
                                    : ""}
                            </div>
                        </div>
                    </div>
                ),
            },
            {
                accessor: "",
                name: t("columns.closed_offers.currency_amount"),
                render: (offer: FetchOffers_fetchOffers_data | null) => (
                    <div className="flex items-center max-w-md">
                        <div className="break-all pl-3 truncate text-ellipsis">
                            <div className="font-medium truncate text-gray-900">
                                {offer?.offer_detail?.currency}
                            </div>
                            <div className="text-gray-500">
                                {numeral(offer?.offer_amount).format("#,#.##")}
                            </div>
                        </div>
                    </div>
                ),
            },
            {
                accessor: "",
                name: t("columns.closed_offers.status"),
                render: (offer: FetchOffers_fetchOffers_data | null) => (
                    <div className="flex items-center">
                        <Badge
                            color={
                                offer?.offer?.expiry_status === "EXPIRED"
                                    ? "red"
                                    : "teal"
                            }>
                            {offer?.offer?.expiry_status ||
                                t("columns.closed_offers.active")}
                        </Badge>
                    </div>
                ),
            },
            {
                accessor: "",
                name: t("columns.closed_offers.payment_status"),
                render: (offer: FetchOffers_fetchOffers_data | null) => (
                    <div className="flex items-center">
                        <Badge
                            color={
                                offer?.offer?.payment_status === "PAID"
                                    ? "blue"
                                    : offer?.offer?.payment_status ===
                                        "PARTPAYMENT"
                                      ? "yellow"
                                      : "red"
                            }>
                            {offer?.offer?.payment_status}
                        </Badge>
                    </div>
                ),
            },
            {
                accessor: "",
                name: t("columns.closed_offers.action"),
                render: (offer: MockOfferStruct | null) => (
                    <OffersActionMenu
                        offer={offer}
                        refresh={refresh}
                        brokerName={offer?.offer?.offer_from as BrokerTypes}
                    />
                ),
            },
        ],
        [t, brokerName, refresh],
    );
};

export const useClaimColumns = () => {
    const { t } = useTranslation();

    return useMemo(
        () => [
            {
                accessor: "claim_amount",
                name: t("columns.all_business.claim_amount"),
                render: (offer: any) => (
                    <div className="flex items-center">
                        {numeral(offer?.claim_amount)?.format("#,#.##")}
                    </div>
                ),
            },
            {
                accessor: "offer_participant_percentage",
                name: t("columns.all_business.participating_percent"),
            },
            {
                accessor: "claim_share",
                name: t("columns.all_business.claim_share"),
            },
            {
                accessor: "claim_date",
                name: t("columns.all_business.claim_date"),
            },
            {
                accessor: "claim_amount",
                name: t("columns.all_business.actions"),
                render: (item: any) => (
                    <ClaimsActions
                        item={item}
                        broker={item?.offer_from as BrokerTypes}
                    />
                ),
            },
        ],
        [t],
    );
};
